import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import type { KanbanStage } from '@/components/kanban/KanbanBoard'

// ============================================================
// TYPES
// ============================================================

export interface TaskKanbanCard {
  id: string
  name: string
  projectName: string | null
  projectColor: string | null
  creatorName: string
  assigneeName: string
  assigneeId: string
  creatorId: string
  priority: string
  status: string

  // F3 (фаза 34): канон группировки доски — columnId. status остаётся теневым (D-3).
  columnId: string
  deadline: string | null
  estimatedMinutes: number | null
  actualMinutes: number
  completedAt: string | null
  lastComment: string | null
  lastCommentAuthor: string | null
}

export interface TaskFilters {
  quickFilter: 'my' | 'from_me' | 'overdue' | null
  projectId: string
  creatorId: string
  assigneeId: string
}

export interface TaskCounts {
  my: number
  from_me: number
  overdue: number
}

interface TasksKanbanState {
  stages: KanbanStage[]
  tasks: Record<string, TaskKanbanCard[]>
  loading: boolean
  filters: TaskFilters
  snapshot: Record<string, TaskKanbanCard[]> | null
  counts: TaskCounts
  // F6: набор задач колонки «На проверке» (постановщик ИЛИ исполнитель), грузится отдельным
  // запросом reviewScope=mine и всегда переопределяет REVIEW-колонку доски вне быстрого фильтра.
  reviewTasks: TaskKanbanCard[]
}

// ============================================================
// CONSTANTS
// ============================================================

// F3 (фаза 34, COL-07): доска НЕ хардкодит столбцы — грузит их из /api/task-columns
// (fetchTaskColumns). Хардкод GTD_STAGES удалён (prohibition плана 34-04). Архивная
// колонка DONE на доску не выводится — исключается в fetchTaskColumns.fulfilled.

const initialFilters: TaskFilters = {
  quickFilter: null,
  projectId: '',
  creatorId: '',
  assigneeId: ''
}

const initialState: TasksKanbanState = {
  stages: [],
  tasks: {},
  loading: false,
  filters: initialFilters,
  snapshot: null,
  counts: { my: 0, from_me: 0, overdue: 0 },
  reviewTasks: []
}

// ============================================================
// HELPERS
// ============================================================

function buildQueryParams(filters: TaskFilters, currentUserId?: string): URLSearchParams {
  const params = new URLSearchParams()

  // Kanban fetches all active tasks (high limit)
  params.set('limit', '200')
  params.set('page', '1')

  // Quick filter logic
  if (filters.quickFilter === 'my' && currentUserId) {
    params.set('assigneeId', currentUserId)
  } else if (filters.quickFilter === 'from_me' && currentUserId) {
    params.set('creatorId', currentUserId)
  } else if (filters.quickFilter === 'overdue') {
    params.set('overdue', 'true')
  }

  // Selector filters (override quickFilter if set)
  if (filters.projectId) params.set('projectId', filters.projectId)
  if (filters.creatorId && filters.quickFilter !== 'from_me') params.set('creatorId', filters.creatorId)
  if (filters.assigneeId && filters.quickFilter !== 'my') params.set('assigneeId', filters.assigneeId)

  return params
}

// F3 (фаза 34, COL-07): группировка по columnId (канон), а не по теневому status.
// Группы создаются для переданных столбцов (DONE среди них нет — он исключён на fetch),
// поэтому задачи из архивной колонки сюда не попадают.
function groupTasksByColumn(
  tasks: TaskKanbanCard[],
  stages: KanbanStage[]
): Record<string, TaskKanbanCard[]> {
  const grouped: Record<string, TaskKanbanCard[]> = {}

  for (const stage of stages) {
    grouped[stage.id] = []
  }

  for (const task of tasks) {
    if (grouped[task.columnId]) {
      grouped[task.columnId].push(task)
    }
  }

  return grouped
}

// F6: колонка «На проверке» (systemRole==='REVIEW') всегда содержит набор reviewTasks
// (постановщик ИЛИ исполнитель), независимо от быстрого фильтра. Если REVIEW-колонки нет
// среди stages — возвращаем tasks без изменений (доска без ревью-колонки).
function applyReviewOverride(
  tasks: Record<string, TaskKanbanCard[]>,
  stages: KanbanStage[],
  reviewTasks: TaskKanbanCard[]
): Record<string, TaskKanbanCard[]> {
  const reviewStage = stages.find((s) => s.systemRole === 'REVIEW')

  if (!reviewStage) return tasks

  return { ...tasks, [reviewStage.id]: [...reviewTasks] }
}

// quick-260704-si5 — экспортируется для TaskPickupView (сетка «Подхвата» переиспользует
// тот же маппинг API-задачи → карточка). Логика не менялась.
export function mapApiTaskToCard(task: Record<string, unknown>): TaskKanbanCard {
  const project = task.project as { name: string; color: string } | null
  const creator = task.creator as { id: string; name: string } | null
  const assignee = task.assignee as { id: string; name: string } | null
  const timeEntries = task.timeEntries as Array<{ minutes: number }> | undefined

  const actualMinutes = timeEntries
    ? timeEntries.reduce((sum, te) => sum + te.minutes, 0)
    : 0

  return {
    id: task.id as string,
    name: task.name as string,
    projectName: project?.name ?? null,
    projectColor: project?.color ?? null,
    creatorName: creator?.name ?? '',
    assigneeName: assignee?.name ?? '',
    assigneeId: assignee?.id ?? (task.assigneeId as string),
    creatorId: creator?.id ?? (task.creatorId as string),
    priority: task.priority as string,
    status: task.status as string,
    columnId: (task.columnId as string | null) ?? '',
    deadline: task.deadline as string | null,
    estimatedMinutes: task.estimatedMinutes as number | null,
    actualMinutes,
    completedAt: task.completedAt as string | null,
    lastComment: (task.lastComment as string) ?? null,
    lastCommentAuthor: (task.lastCommentAuthor as string) ?? null
  }
}

// ============================================================
// THUNKS
// ============================================================

// F3 (фаза 34, COL-07): динамические столбцы доски из конфигурации. Архивная колонка
// DONE (systemRole==='DONE') исключается — она вне доски. Порядок — по sortOrder.
export const fetchTaskColumns = createAsyncThunk('tasksKanban/fetchColumns', async () => {
  const res = await fetch('/api/task-columns')
  const json = await res.json()

  if (!json.success) {
    throw new Error(json.error ?? 'Failed to fetch task columns')
  }

  const columns = json.data as Array<{
    id: string
    name: string
    color: string
    sortOrder: number
    systemRole: string | null
  }>

  const stages: KanbanStage[] = columns
    .filter((c) => c.systemRole !== 'DONE')
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((c) => ({
      id: c.id,
      name: c.name,
      color: c.color,
      sortOrder: c.sortOrder,
      systemRole: c.systemRole
    }))

  return stages
})

export const fetchTasks = createAsyncThunk(
  'tasksKanban/fetchTasks',
  async (currentUserId: string | undefined, { getState }) => {
    const state = getState() as { tasksKanbanReducer: TasksKanbanState }
    const { filters } = state.tasksKanbanReducer
    const params = buildQueryParams(filters, currentUserId)

    const res = await fetch(`/api/tasks?${params}`)
    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to fetch tasks')
    }

    return (json.data as Record<string, unknown>[]).map(mapApiTaskToCard)
  }
)

// F6 (quick-260702-vcb): набор колонки «На проверке» — все ON_REVIEW-задачи, где текущий
// пользователь постановщик ИЛИ исполнитель. Отдельный запрос reviewScope=mine (dedicated
// where на сервере, минуя быстрый фильтр). Reducer переопределяет им REVIEW-колонку доски.
export const fetchReviewTasks = createAsyncThunk('tasksKanban/fetchReviewTasks', async () => {
  const res = await fetch('/api/tasks?reviewScope=mine&limit=200')
  const json = await res.json()

  if (!json.success) {
    throw new Error(json.error ?? 'Failed to fetch review tasks')
  }

  return (json.data as Record<string, unknown>[]).map(mapApiTaskToCard)
})

// F3 (фаза 34, COL-07/CLOSE-04): перемещение по columnId. Возвращает ФАКТИЧЕСКУЮ карточку
// из ответа API — сервер мог увести задачу не туда, куда тащили (self-close: перенос в
// REVIEW самоназначенной задачи закрывает её в DONE). Reducer применяет реальную колонку.
export const moveTaskStatus = createAsyncThunk(
  'tasksKanban/moveStatus',
  async ({ taskId, columnId }: { taskId: string; columnId: string }) => {
    const res = await fetch(`/api/tasks/${taskId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ columnId })
    })

    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to move task')
    }

    return mapApiTaskToCard(json.data as Record<string, unknown>)
  }
)

// F2 (quick-260703-ltl): реордер карточек ВНУТРИ одного столбца канбана (persist порядка
// через Task.sortOrder). Оптимистично переставляем колонку по orderedIds со snapshot-откатом
// (паттерн moveTaskStatus). columnId нужен только для оптимистичной перестановки локального
// набора; сервер ставит sortOrder по индексу в orderedIds.
export const reorderTasks = createAsyncThunk(
  'tasksKanban/reorder',
  async ({ orderedIds }: { columnId: string; orderedIds: string[] }) => {
    const res = await fetch('/api/tasks/reorder', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderedIds })
    })

    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to reorder tasks')
    }

    return true
  }
)

export const quickCreateTask = createAsyncThunk(
  'tasksKanban/quickCreate',
  async (name: string) => {
    const res = await fetch('/api/tasks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name })
    })

    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to create task')
    }

    return mapApiTaskToCard(json.data as Record<string, unknown>)
  }
)

// #8: серверные счётчики фильтров одним батчем. Фетчатся на маунте/поллинге/после мутаций,
// НЕ на каждый клик по фильтру (клиентская фильтрация видимых карточек не трогает БД).
export const fetchTaskCounts = createAsyncThunk('tasksKanban/fetchCounts', async () => {
  const res = await fetch('/api/tasks/counts')
  const json = await res.json()

  if (!json.success) {
    throw new Error(json.error ?? 'Failed to fetch task counts')
  }

  const data = json.data as { my: number; fromMe: number; overdue: number }

  return { my: data.my, from_me: data.fromMe, overdue: data.overdue }
})

export const createTask = createAsyncThunk(
  'tasksKanban/createTask',
  async (data: { name: string; [key: string]: unknown }) => {
    const res = await fetch('/api/tasks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })

    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to create task')
    }

    return mapApiTaskToCard(json.data as Record<string, unknown>)
  }
)

// ============================================================
// SLICE
// ============================================================

export const tasksKanbanSlice = createSlice({
  name: 'tasksKanban',
  initialState,
  reducers: {
    setQuickFilter: (state, action: PayloadAction<'my' | 'from_me' | 'overdue' | null>) => {
      return {
        ...state,
        filters: { ...state.filters, quickFilter: action.payload }
      }
    },
    setProjectFilter: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        filters: { ...state.filters, projectId: action.payload }
      }
    },
    setCreatorFilter: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        filters: { ...state.filters, creatorId: action.payload }
      }
    },
    setAssigneeFilter: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        filters: { ...state.filters, assigneeId: action.payload }
      }
    },
    clearFilters: (state) => {
      return {
        ...state,
        filters: initialFilters
      }
    }
  },
  extraReducers: (builder) => {
    // fetchTaskColumns — динамические столбцы доски (COL-07). После загрузки
    // перегруппируем уже имеющиеся задачи по columnId под новый набор столбцов.
    builder.addCase(fetchTaskColumns.fulfilled, (state, action) => {
      const stages = action.payload
      const allTasks = Object.values(state.tasks).flat()
      const grouped = groupTasksByColumn(allTasks, stages)

      // F6: после регруппировки под новый набор столбцов переопределяем REVIEW-колонку.
      return { ...state, stages, tasks: applyReviewOverride(grouped, stages, state.reviewTasks) }
    })

    // fetchTaskCounts — серверные счётчики фильтров (#8)
    builder.addCase(fetchTaskCounts.fulfilled, (state, action) => {
      return { ...state, counts: action.payload }
    })

    // fetchTasks
    builder.addCase(fetchTasks.pending, (state) => {
      return { ...state, loading: true }
    })

    builder.addCase(fetchTasks.fulfilled, (state, action) => {
      const grouped = groupTasksByColumn(action.payload, state.stages)

      // F6: REVIEW-колонка всегда = reviewTasks (постановщик|исполнитель), не быстрый фильтр.
      const tasks = applyReviewOverride(grouped, state.stages, state.reviewTasks)

      return { ...state, tasks, loading: false }
    })

    builder.addCase(fetchTasks.rejected, (state) => {
      return { ...state, loading: false }
    })

    // F6: fetchReviewTasks — сохраняем набор и сразу переопределяем REVIEW-колонку доски.
    builder.addCase(fetchReviewTasks.fulfilled, (state, action) => {
      return {
        ...state,
        reviewTasks: action.payload,
        tasks: applyReviewOverride(state.tasks, state.stages, action.payload)
      }
    })

    // moveTaskStatus -- optimistic update with snapshot for rollback (по columnId)
    builder.addCase(moveTaskStatus.pending, (state, action) => {
      const { taskId, columnId: toColumnId } = action.meta.arg

      // Save snapshot for rollback
      const snapshot: Record<string, TaskKanbanCard[]> = {}

      for (const [key, cards] of Object.entries(state.tasks)) {
        snapshot[key] = [...cards]
      }

      // Find and move task optimistically (guess: target column)
      let movedTask: TaskKanbanCard | null = null
      const updatedTasks: Record<string, TaskKanbanCard[]> = {}

      for (const [sId, cards] of Object.entries(state.tasks)) {
        const filtered = cards.filter((t) => {
          if (t.id === taskId) {
            movedTask = { ...t, columnId: toColumnId }

            return false
          }

          return true
        })

        updatedTasks[sId] = filtered
      }

      if (movedTask && updatedTasks[toColumnId]) {
        updatedTasks[toColumnId] = [...updatedTasks[toColumnId], movedTask]
      }

      return { ...state, tasks: updatedTasks, snapshot }
    })

    // CLOSE-04: применяем ФАКТИЧЕСКУЮ колонку из ответа API, а не optimistic-guess.
    // Сервер мог увести задачу не в ту колонку, куда тащили (self-close: перенос в
    // REVIEW самоназначенной задачи закрывает её в DONE → она уходит с доски).
    builder.addCase(moveTaskStatus.fulfilled, (state, action) => {
      const card = action.payload

      // Снимаем карточку отовсюду (она была размещена оптимистично).
      const updatedTasks: Record<string, TaskKanbanCard[]> = {}

      for (const [sId, cards] of Object.entries(state.tasks)) {
        updatedTasks[sId] = cards.filter((t) => t.id !== card.id)
      }

      // Вставляем в фактическую колонку, только если она на доске (DONE — вне доски,
      // её columnId нет среди stages, значит задача архивирована и с доски исчезает).
      if (updatedTasks[card.columnId]) {
        updatedTasks[card.columnId] = [...updatedTasks[card.columnId], card]
      }

      return { ...state, tasks: updatedTasks, snapshot: null }
    })

    builder.addCase(moveTaskStatus.rejected, (state) => {
      // Rollback to snapshot
      if (state.snapshot) {
        return { ...state, tasks: state.snapshot, snapshot: null }
      }

      return state
    })

    // F2: reorderTasks — оптимистичная перестановка колонки со snapshot-откатом.
    builder.addCase(reorderTasks.pending, (state, action) => {
      const { columnId, orderedIds } = action.meta.arg

      // Snapshot всех колонок для отката.
      const snapshot: Record<string, TaskKanbanCard[]> = {}

      for (const [key, cards] of Object.entries(state.tasks)) {
        snapshot[key] = [...cards]
      }

      const columnCards = state.tasks[columnId]

      if (!columnCards) {
        return { ...state, snapshot }
      }

      // Переставляем карточки колонки по orderedIds (id→карточка, отфильтровать undefined).
      const byId = new Map(columnCards.map((c) => [c.id, c]))
      const reordered = orderedIds.map((id) => byId.get(id)).filter((c): c is TaskKanbanCard => c !== undefined)

      // Карточки, не попавшие в orderedIds (защита от рассинхрона) — в хвост в прежнем порядке.
      const missing = columnCards.filter((c) => !orderedIds.includes(c.id))

      return { ...state, tasks: { ...state.tasks, [columnId]: [...reordered, ...missing] }, snapshot }
    })

    builder.addCase(reorderTasks.fulfilled, (state) => {
      return { ...state, snapshot: null }
    })

    builder.addCase(reorderTasks.rejected, (state) => {
      if (state.snapshot) {
        return { ...state, tasks: state.snapshot, snapshot: null }
      }

      return state
    })

    // quickCreateTask — новая задача попадает в свою колонку (INTAKE) по columnId.
    builder.addCase(quickCreateTask.fulfilled, (state, action) => {
      const task = action.payload

      if (!state.tasks[task.columnId]) return state

      const columnTasks = state.tasks[task.columnId] ?? []

      const updatedTasks = {
        ...state.tasks,
        [task.columnId]: [...columnTasks, task]
      }

      return { ...state, tasks: updatedTasks }
    })

    // createTask — то же: группировка по columnId.
    builder.addCase(createTask.fulfilled, (state, action) => {
      const task = action.payload

      if (!state.tasks[task.columnId]) return state

      const columnTasks = state.tasks[task.columnId] ?? []

      const updatedTasks = {
        ...state.tasks,
        [task.columnId]: [...columnTasks, task]
      }

      return { ...state, tasks: updatedTasks }
    })
  }
})

export const {
  setQuickFilter,
  setProjectFilter,
  setCreatorFilter,
  setAssigneeFilter,
  clearFilters
} = tasksKanbanSlice.actions

export default tasksKanbanSlice.reducer
